// Google Drive mirror status: which account, root folder link, and the last
// copy per institution. When Drive is not configured, points to the setup doc.
import React from 'react';
import { Cloud, CloudOff, ExternalLink, AlertTriangle, CheckCircle } from 'lucide-react';
import type { DriveStatus } from '@/types/tenant-export.types';

interface TenantExportDrivePanelProps {
  status: DriveStatus | null;
  loading: boolean;
}

export const TenantExportDrivePanel: React.FC<TenantExportDrivePanelProps> = ({ status, loading }) => {
  if (loading && !status) {
    return <div className="rounded-xl border border-gray-200 bg-white p-5 text-sm text-gray-500">Vérification de Google Drive…</div>;
  }
  if (!status) return null;

  if (!status.configured) {
    return (
      <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-5">
        <div className="flex items-start gap-3">
          <CloudOff className="mt-0.5 shrink-0 text-gray-400" size={20} />
          <div className="text-sm text-gray-700">
            <p className="font-medium text-gray-900">Copie Google Drive non configurée</p>
            <p className="mt-1">
              Les exports restent dans le stockage NEXEDU (copié chaque nuit hors site). Pour recevoir aussi une copie dans un Google Drive,
              suivre <code className="rounded bg-white px-1 py-0.5 text-xs">docs/GOOGLE-DRIVE-SYNC.md</code> (≈10 minutes), puis ajouter les trois
              variables <code className="rounded bg-white px-1 py-0.5 text-xs">GOOGLE_DRIVE_*</code> sur le serveur.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <Cloud className="mt-0.5 shrink-0 text-emerald-600" size={20} />
          <div className="text-sm">
            <p className="font-medium text-gray-900">Copie Google Drive active</p>
            <p className="mt-0.5 text-gray-600">
              Compte <span className="font-medium">{status.account ?? '—'}</span> · dossier <span className="font-medium">{status.rootFolderName}</span>
              {status.rootFolderLink && (
                <a href={status.rootFolderLink} target="_blank" rel="noreferrer" className="ml-2 inline-flex items-center gap-1 text-blue-600 hover:underline">
                  Ouvrir <ExternalLink size={13} />
                </a>
              )}
            </p>
            {status.error && (
              <p className="mt-1 flex items-start gap-1.5 text-red-600"><AlertTriangle size={14} className="mt-0.5 shrink-0" /> {status.error}</p>
            )}
          </div>
        </div>
      </div>

      {status.tenants.length > 0 && (
        <ul className="mt-4 divide-y divide-gray-100 rounded-lg border border-gray-100">
          {status.tenants.map((t) => (
            <li key={t.tenantId} className="flex flex-wrap items-center justify-between gap-2 px-4 py-2.5 text-sm">
              <div className="flex items-center gap-2">
                {t.lastError ? <AlertTriangle size={15} className="text-red-500" /> : <CheckCircle size={15} className="text-emerald-500" />}
                <span className="font-medium text-gray-900">{t.tenantName}</span>
                {t.institutionFolderLink && (
                  <a href={t.institutionFolderLink} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline">
                    Dossier <ExternalLink size={12} />
                  </a>
                )}
              </div>
              <div className="text-xs text-gray-500">
                {t.lastError
                  ? <span className="text-red-600" title={t.lastError}>Dernière copie en échec</span>
                  : <>Dernier passage {t.lastRunDate ?? '—'} · {t.documentsOnDrive.toLocaleString('fr-FR')} documents sur Drive</>}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
